import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Testimonial {
  id: string;
  client_id: string | null;
  project_id: string | null;
  client_project_id: string | null;
  author_name: string;
  author_role: string | null;
  author_avatar: string | null;
  quote: string;
  rating: number | null;
  approved: boolean;
  public_token: string | null;
  submitted_at: string | null;
  created_at: string;
}

export function useTestimonials() {
  return useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials" as never)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Testimonial[];
    },
  });
}

// Public site: anon only has column-level SELECT on these fields
// (migration 20260925120000) — never public_token / client ids.
export type PublicTestimonial = Pick<
  Testimonial,
  "id" | "author_name" | "author_role" | "author_avatar" | "quote" | "rating"
>;

export function usePublicTestimonials() {
  return useQuery({
    queryKey: ["public-testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials" as never)
        .select("id, author_name, author_role, author_avatar, quote, rating")
        .eq("approved", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as PublicTestimonial[];
    },
  });
}

export function useApproveTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const { error } = await supabase
        .from("testimonials" as never)
        .update({ approved } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      qc.invalidateQueries({ queryKey: ["public-testimonials"] });
      toast.success("Depoimento atualizado");
    },
    onError: () => toast.error("Não foi possível atualizar o depoimento"),
  });
}

export function useCreateTestimonialRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      client_project_id?: string;
      client_id?: string;
      author_name: string;
      author_role?: string;
    }) => {
      const { data: r, error } = await supabase
        .from("testimonials" as never)
        .insert({ ...data, quote: "", approved: false } as never)
        .select()
        .single();
      if (error) throw error;
      return r as Testimonial;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["testimonials"] }),
    onError: () => toast.error("Não foi possível gerar o link de depoimento"),
  });
}

// ─── fluxo público /feedback/:token (anon) ──────────────────────────────────
// O anônimo não lê nem escreve a tabela: get_testimonial_by_token() e
// submit_testimonial() (SECURITY DEFINER) validam token, expiração e
// "ainda não respondido" (docs/specs/flows/depoimento.md I1/I2).

/** What /feedback/:token gets back — never the token itself nor the approval flag. */
export interface TestimonialInvite {
  id: string;
  author_name: string;
  author_role: string | null;
  rating: number | null;
  submitted_at: string | null;
}

/**
 * Public read by token goes through the SECURITY DEFINER RPC
 * (20260914020000): an anon SELECT policy can't know which token the client
 * holds, so it let anyone list every pending invite.
 */
export function useTestimonialByToken(token: string | undefined) {
  return useQuery({
    queryKey: ["testimonial-by-token", token],
    enabled: !!token,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_testimonial_by_token" as never, { p_token: token! } as never)
        .maybeSingle();
      if (error) throw error;
      return (data as TestimonialInvite | null) ?? null;
    },
  });
}

export interface SubmitTestimonialInput {
  token: string;
  quote: string;
  author_name: string;
  author_role: string;
  rating: number;
}

export function useSubmitTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      token,
      quote,
      author_name,
      author_role,
      rating,
    }: SubmitTestimonialInput) => {
      const { data, error } = await supabase.rpc(
        "submit_testimonial" as never,
        {
          p_token: token,
          p_quote: quote,
          p_author_name: author_name,
          p_author_role: author_role,
          p_rating: rating,
        } as never,
      );
      if (error) throw error;
      // false = invite not pending anymore (already sent, expired or unknown token).
      if (!data) throw new Error("testimonial_not_pending");
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["testimonial-by-token", v.token] }),
  });
}
